<div
  class="box-border flex w-full flex-col bg-inherit text-base"
  [ngClass]="{
    'cursor-not-allowed opacity-60': ngControl.control!.disabled,
    'bg-primary-on': !corporateDesignService.isDarkModeActive(),
  }"
  [ngStyle]="{
    background: inputBackground(),
  }">
  @let hasError = ngControl.errors && ngControl.touched;

  @if (label()) {
    <label
      class="mb-2 block"
      [attr.data-testid]="id() | appendTestId: 'label'"
      [attr.for]="searchable() ? (id() | appendTestId: 'search') : undefined"
      [ngClass]="corporateDesignService.baseFont()">
      {{ formattedLabel() }}
    </label>
  }

  @if (searchable()) {
    <div class="relative mb-2">
      <span
        class="material-symbols-rounded text-input-label pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
        aria-hidden="true">
        search
      </span>
      <input
        [attr.data-bwignore]="ignoreBitwardenAutofill ? 'true' : null"
        type="search"
        autocomplete="off"
        [id]="id() | appendTestId: 'search'"
        [formControl]="searchControl"
        [attr.data-testid]="id() | appendTestId: 'search'"
        [placeholder]="searchPlaceholder()"
        class="focus:border-input-focus border-input-ring border-input rounded-input h-10 w-full bg-transparent pr-3 pl-10 focus:ring-0 focus:outline-none"
        [ngClass]="corporateDesignService.baseFont()" />
    </div>
  }

  <div
    class="border-input-ring border-input rounded-input overflow-hidden bg-inherit"
    [ngClass]="{
      'border-input-error': hasError,
    }"
    [ngStyle]="{
      borderRadius: borderRounding(),
    }"
    [ngClass]="corporateDesignService.baseFont()">
    <div
      class="flex min-h-10 items-center px-3 py-2 font-semibold"
      [attr.data-testid]="id() | appendTestId: 'list-label'">
      <span
        class="material-symbols-rounded invisible mr-3 text-lg"
        aria-hidden="true">
        drag_handle
      </span>
      <span>{{ listLabel() }}</span>
    </div>

    <div>
      @for (entry of visibleEntries(); track entry.index) {
        @if (!isEditing() || editingIndex() !== entry.index) {
          <div
            class="border-input-ring flex min-h-10 items-center gap-2 border-t px-3 py-2"
            [attr.data-testid]="id() | appendTestId: 'item-' + entry.index">
            <span
              class="material-symbols-rounded text-input-label mr-3 text-lg"
              aria-hidden="true">
              drag_handle
            </span>
            <span class="min-w-0 flex-1 overflow-hidden text-ellipsis">
              {{ entry.value }}
            </span>
            <button
              type="button"
              title="Eintrag bearbeiten"
              [disabled]="ngControl.control!.disabled || isEditing()"
              [attr.data-testid]="id() | appendTestId: 'edit-' + entry.index"
              class="hover:bg-standard-hover-main/30 ml-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm disabled:cursor-not-allowed"
              (click)="startEditing(entry)">
              <span class="text-input-label material-symbols-rounded text-xl" aria-hidden="true">
                edit
              </span>
            </button>
            <button
              type="button"
              title="Eintrag löschen"
              [disabled]="ngControl.control!.disabled || isEditing()"
              [attr.data-testid]="id() | appendTestId: 'delete-' + entry.index"
              class="hover:bg-error-light/30 hover:text-error-on ml-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm disabled:cursor-not-allowed"
              (click)="deleteItem(entry.index)">
              <span
                class="material-symbols-rounded text-input-error text-xl"
                aria-hidden="true">
                delete
              </span>
            </button>
          </div>
        } @else {
          <ng-container *ngTemplateOutlet="itemEditor"></ng-container>
        }
      }
    </div>

    @if (isEditing() && editingIndex() === undefined) {
      <ng-container *ngTemplateOutlet="itemEditor"></ng-container>
    }
  </div>

  <div class="mt-2 flex items-center justify-end gap-2">
    <aw-button type="button" [isDisabled]="ngControl.control!.disabled || isEditing()" [style]="'tertiary'" id="sortButton"
      [buttonSize]="'small'"
      (click)="toggleSortDirection()">
      <span class="material-symbols-rounded mr-1 text-xl" aria-hidden="true">
        sort
      </span>
      <span>{{ cmsTextProvider.sortiment.sortPlaceholder }}</span>
    </aw-button>
    <aw-button type="button" [isDisabled]="ngControl.control!.disabled || isEditing()" [style]="'primary-alt'" id="addButton"
      [buttonSize]="'small'"
      (click)="startAdding()">
      <span class="material-symbols-rounded mr-1 text-xl" aria-hidden="true">
        add
      </span>
      <span>{{ cmsTextProvider.shared.add }}</span>
    </aw-button>
  </div>

  @if (hasError && !errorTemplate()) {
    <aw-error-message [attr.data-testid]="id() | appendTestId: 'error'">
      <ng-content></ng-content>
    </aw-error-message>
  }
  @if (hasError && errorTemplate()) {
    <aw-error-message [attr.data-testid]="id() | appendTestId: 'error'">
      <ng-container *ngTemplateOutlet="errorTemplate()!"></ng-container>
    </aw-error-message>
  }
</div>

<ng-template #itemEditor>
  <div
    class="border-input-ring flex min-h-10 items-center border-t px-3 py-1"
    [attr.data-testid]="id() | appendTestId: 'editor'">
    <span
      class="material-symbols-rounded text-input-label mr-3 text-lg"
      aria-hidden="true">
      drag_handle
    </span>
    <input
      [attr.data-bwignore]="ignoreBitwardenAutofill ? 'true' : null"
      [type]="itemType()"
      [formControl]="itemControl"
      [placeholder]="itemPlaceholder()"
      [attr.data-testid]="id() | appendTestId: 'item-input'"
      class="focus:border-input-focus border-input-ring min-w-0 flex-1 rounded-sm border-1 bg-transparent px-3 py-1.5 focus:ring-0 focus:outline-none"
      (keydown)="handleItemKeydown($event)" />
    <button
      type="button"
      title="Übernehmen"
      [disabled]="!hasValidItemValue()"
      [attr.data-testid]="id() | appendTestId: 'confirm'"
      class="hover:bg-standard-hover-main/30 ml-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm disabled:cursor-not-allowed disabled:opacity-50"
      (click)="saveItem()">
      <span class="text-input-label material-symbols-rounded text-xl" aria-hidden="true">
        check
      </span>
    </button>
    <button
      type="button"
      title="Abbrechen"
      [attr.data-testid]="id() | appendTestId: 'cancel'"
      class="hover:bg-standard-hover-main/30 ml-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm"
      (click)="cancelEditing()">
      <span class="text-input-label material-symbols-rounded text-xl" aria-hidden="true">
        close
      </span>
    </button>
  </div>
</ng-template>
